import type {
  JsonValue,
  MapControlResult,
  MapExecutionConfig,
  MapItemOutcome,
  MapProgress,
  NodeStruct,
  RegionNodeResult,
} from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';
import {
  latestMapProgress,
  parseMapControlResult,
} from '@/pages/workflow/helper/map-control';
import {
  getSkipExplanation,
  skippedSentence,
} from '@/pages/workflow/helper/decision-trace';

/**
 * Plain-language readings of a Map / For Each node: its limits as a
 * sentence, its progress as a sentence and a fraction, and its finished
 * run as rows a person can scan. Pure; the panel renders these.
 */

export interface MapLimitsInWords {
  maxItems: number;
  maxItemKb: number;
  maxConcurrency: number;
  sentence: string;
}

const kb = (bytes: number | undefined, fallback: number): number =>
  Math.max(1, Math.round((bytes ?? fallback) / 1024));

/** "Up to 256 items, 32 KB each, 8 at a time." from the catalog's limits. */
export const mapLimitsInWords = (
  struct: NodeStruct | undefined,
  config: MapExecutionConfig | null,
): MapLimitsInWords => {
  const limits = struct?.map_runtime_limits;
  const maxItems = Math.min(
    config?.max_items ?? Number.POSITIVE_INFINITY,
    limits?.max_items ?? 256,
  );
  const maxConcurrency = Math.min(
    config?.max_concurrency ?? Number.POSITIVE_INFINITY,
    limits?.max_concurrency ?? 8,
  );
  const maxItemKb = kb(limits?.max_item_bytes, 32_768);
  return {
    maxItems,
    maxItemKb,
    maxConcurrency,
    sentence: `Up to ${maxItems} items, ${maxItemKb} KB each, ${maxConcurrency} at a time.`,
  };
};

export interface MapProgressInWords {
  input: number;
  done: number;
  failed: number;
  fraction: number;
  sentence: string;
  state: 'idle' | 'running' | 'finished';
}

/** Progress from the live events, or the finished result's counts. */
export const mapProgressInWords = (
  result: unknown,
  events: NodeStatusEvent[] | undefined,
  isExecuting: boolean,
): MapProgressInWords | null => {
  const finished = parseMapControlResult(result);
  const live: MapProgress | null = latestMapProgress(events);
  const progress =
    live ??
    (finished
      ? {
          input: finished.counts.input,
          admitted: finished.counts.input,
          completed: finished.counts.completed,
          failed: finished.counts.failed,
        }
      : null);
  if (!progress) return null;
  const done = progress.completed + progress.failed;
  const fraction = progress.input > 0 ? done / progress.input : 0;
  const state =
    finished && !isExecuting
      ? 'finished'
      : isExecuting
        ? 'running'
        : 'finished';
  const failedText = progress.failed ? `, ${progress.failed} failed` : '';
  const sentence =
    state === 'running'
      ? `Item ${Math.min(done + 1, progress.input)} of ${progress.input}${failedText}`
      : progress.failed
        ? `${progress.completed} of ${progress.input} items done${failedText}`
        : `All ${progress.input} items done`;
  return {
    input: progress.input,
    done,
    failed: progress.failed,
    fraction,
    sentence,
    state,
  };
};

/** What one body node did for one item (For-each bodies, config 1.1). */
export interface MapItemStep {
  nodeId: string;
  name: string;
  status: 'completed' | 'failed' | 'skipped';
  preview: string;
  value: JsonValue | null;
  /** 'skipped: branch «odd» not taken (Parity took «even»)' for skipped nodes. */
  skipSentence: string | null;
}

export interface MapItemRow {
  index: number;
  status: 'completed' | 'failed';
  preview: string;
  errorCode: string | null;
  errorMessage: string | null;
  value: JsonValue | null;
  /** Body steps in run order; empty for a single-step Map. */
  steps: MapItemStep[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The per-node map of a region outcome, whichever envelope the carrier used. */
export const regionResultsOf = (
  outcome: MapItemOutcome,
): Record<string, RegionNodeResult> => {
  const region = (outcome as { region?: unknown }).region;
  if (!isRecord(region)) return {};
  const inner = region.region_results ?? region.results;
  return (isRecord(inner) ? inner : region) as Record<string, RegionNodeResult>;
};

const stepFrom = (
  nodeId: string,
  entry: RegionNodeResult,
  nodeNames: Record<string, string>,
): MapItemStep => {
  const skip = getSkipExplanation(entry);
  const value = (entry.output ?? (entry as { value?: JsonValue }).value) as
    | JsonValue
    | undefined;
  const status: MapItemStep['status'] = skip
    ? 'skipped'
    : entry.status === 'failed' || entry.error
      ? 'failed'
      : 'completed';
  return {
    nodeId,
    name: nodeNames[nodeId] ?? nodeId,
    status,
    preview: status === 'completed' ? previewValue(value) : '',
    value: status === 'completed' ? (value ?? null) : null,
    skipSentence: skip ? skippedSentence(skip) : null,
  };
};

/** Body steps for one item, in the body's run order (unknown ids last). */
export const mapItemSteps = (
  outcome: MapItemOutcome,
  options: { nodeNames?: Record<string, string>; order?: string[] } = {},
): MapItemStep[] => {
  const results = regionResultsOf(outcome);
  const order = options.order ?? [];
  const ids = [
    ...order.filter((id) => id in results),
    ...Object.keys(results)
      .filter((id) => !order.includes(id))
      .sort(),
  ];
  return ids
    .filter((id) => isRecord(results[id]))
    .map((id) => stepFrom(id, results[id], options.nodeNames ?? {}));
};

export const previewValue = (value: unknown, max = 80): string => {
  if (value === undefined) return '';
  let text: string;
  if (typeof value === 'string') text = value;
  else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const ITEM_ERROR_WORDS: Record<string, string> = {
  map_item_bytes_limit: 'The item is larger than the per-item limit.',
  map_item_result_bytes_limit:
    'The result for this item is larger than the per-item limit.',
  map_item_timeout: 'The body took longer than the per-item time limit.',
  map_value_not_json: 'The body returned something that is not plain JSON.',
};

/** Rows for the item inspector, in input order, failures explained. */
export const mapItemRows = (
  result: unknown,
  options: { nodeNames?: Record<string, string>; order?: string[] } = {},
): MapItemRow[] => {
  const parsed: MapControlResult | null = parseMapControlResult(result);
  if (!parsed) return [];
  return [...parsed.results]
    .sort((a: MapItemOutcome, b: MapItemOutcome) => a.index - b.index)
    .map((outcome) => ({
      steps: mapItemSteps(outcome, options),
      index: outcome.index,
      status: outcome.status,
      preview:
        outcome.status === 'completed' ? previewValue(outcome.value) : '',
      errorCode: outcome.error?.code ?? null,
      errorMessage: outcome.error
        ? (ITEM_ERROR_WORDS[outcome.error.code] ??
          `Failed (${outcome.error.code.replace(/_/g, ' ')}).`)
        : null,
      value: outcome.status === 'completed' ? (outcome.value ?? null) : null,
    }));
};

/** What one body node did across the items of the last run. */
export interface ForEachNodeRun {
  items: Array<{ index: number; step: MapItemStep }>;
  ran: number;
  skipped: number;
  failed: number;
  total: number;
}

export const forEachNodeRun = (
  carrierResult: unknown,
  nodeId: string,
): ForEachNodeRun | null => {
  const parsed = parseMapControlResult(carrierResult);
  if (!parsed) return null;
  const items = [...parsed.results]
    .sort((a, b) => a.index - b.index)
    .flatMap((outcome) => {
      const entry = regionResultsOf(outcome)[nodeId];
      return isRecord(entry)
        ? [{ index: outcome.index, step: stepFrom(nodeId, entry, {}) }]
        : [];
    });
  if (items.length === 0) return null;
  return {
    items,
    ran: items.filter((item) => item.step.status === 'completed').length,
    skipped: items.filter((item) => item.step.status === 'skipped').length,
    failed: items.filter((item) => item.step.status === 'failed').length,
    total: parsed.counts.input,
  };
};

/** "Stops at the first failure" / "Runs every item and reports the failures". */
export const failureModeInWords = (
  mode: MapExecutionConfig['failure_mode'] | undefined,
): string =>
  mode === 'all_settled'
    ? 'Runs every item and reports the ones that failed.'
    : 'Stops at the first item that fails.';

/** What the body does, in a sentence, or that there is none yet. */
export const bodyInWords = (
  config: MapExecutionConfig | null,
  bodyStruct: NodeStruct | undefined,
): string => {
  if (!config?.body) {
    return 'No step chosen yet. Today the only step a Map can run is Map Item, which passes each item (optionally with its position) straight through.';
  }
  const name = bodyStruct?.display_name ?? config.body.catalog_node_id;
  const projection = config.body.static_params?.projection;
  if (projection === 'item_with_index') {
    return `Runs ${name} once per item and returns each item together with its position.`;
  }
  return `Runs ${name} once per item and returns the items in input order.`;
};

/** "For each" when the Map runs a part of the canvas (a collector is wired), else "Map". */
export const mapPanelTitle = (regionMode: boolean): string =>
  regionMode ? 'For each' : 'Map';
